import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Copy, Loader2, EyeOff } from "lucide-react";

const tag = (c) => c.acao === "fica visível" ? "bg-emerald-100 text-emerald-700" : c.acao === "já oculto" ? "bg-slate-100 text-slate-600" : "bg-amber-100 text-amber-800";

const fmt = (d) => d ? new Date(String(d).length === 10 ? `${d}T12:00:00` : d).toLocaleDateString("pt-BR") : "—";

export const DuplicatesDialog = ({ open, onClose, onApplied }) => {
  const [p, setP] = useState(null);
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { return; }
    setP(null); setPhrase("");
    api.get("/admin/duplicates/preview").then(r => setP(r.data)).catch(e => toast.error(e.response?.data?.detail || "Erro ao gerar prévia"));
  }, [open]);

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/duplicates/apply", { confirm: phrase, expected: p.a_ocultar });
      toast.success(`${data.ocultados} cadastro(s) movido(s) para Repetidos`);
      onApplied && onApplied(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="duplicates-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Copy className="w-5 h-5 text-rose-600"/>Alunos com nome repetido — prévia</DialogTitle></DialogHeader>
        {!p ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-rose-600"/></div> : (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Mesmo nome em qualquer unidade. Fica visível quem está ativo ou tem vendas; se nenhum tiver, fica o mais recente. Os demais vão para <strong>Repetidos (ocultos)</strong>, visível só para o administrador. Nada é apagado.</p>
            <div className="flex gap-3 text-sm" data-testid="duplicates-summary">
              <span className="px-3 py-1.5 rounded-lg bg-slate-100"><strong>{p.grupos_com_repetidos}</strong> nomes repetidos</span>
              <span className="px-3 py-1.5 rounded-lg bg-amber-100 text-amber-800"><strong data-testid="duplicates-to-hide">{p.a_ocultar}</strong> cadastros a ocultar</span>
            </div>
            <div className="border rounded-lg divide-y max-h-80 overflow-y-auto">
              {p.grupos.map((g, i) => (
                <div key={i} className="p-3" data-testid={`duplicates-group-${i}`}>
                  <div className="font-semibold text-sm text-slate-900 mb-1">{g.nome}</div>
                  {g.cadastros.map(c => (
                    <div key={c.id} className="flex flex-wrap items-center gap-2 text-xs text-slate-600 py-0.5">
                      <span className={`px-2 py-0.5 rounded-full font-bold ${tag(c)}`}>{c.acao}</span>
                      <span>{c.unidade}</span><span>· CPF {c.cpf || "—"}</span><span>· Vence {fmt(c.vencimento)}</span>
                      <span>· {c.ativo ? "ativo" : "inativo"}</span><span>· {c.tem_vendas ? "com vendas" : "sem vendas"}</span>
                    </div>
                  ))}
                </div>
              ))}
              {p.grupos.length === 0 && <div className="p-6 text-center text-sm text-slate-500">Nenhum repetido para ocultar.</div>}
            </div>
            {p.grupos_com_repetidos > p.grupos.length && <p className="text-xs text-slate-500">Mostrando os primeiros {p.grupos.length} nomes.</p>}
            {p.a_ocultar > 0 && (
              <div className="space-y-2">
                <label className="text-sm text-slate-700">Para confirmar, digite <strong>{p.frase}</strong></label>
                <Input value={phrase} onChange={(e) => setPhrase(e.target.value)} data-testid="duplicates-confirm-input"/>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={onClose}>Cancelar</Button>
                  <Button disabled={busy || phrase !== p.frase} onClick={apply} className="bg-rose-600 hover:bg-rose-700 font-bold" data-testid="duplicates-apply-button">
                    <EyeOff className="w-4 h-4 mr-2"/>{busy ? "Aplicando..." : `Ocultar ${p.a_ocultar} cadastro(s)`}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
